import * as React from "react"
import { useDialogLayer } from "@/components/ui/dialog-layer"

/**
 * Portal floating layers (popovers, dropdowns, tooltips, selects). Inside a dialog they go into that
 * dialog's own layer so they stack above it; elsewhere into #dialog-layer. FluentThemeRoot creates #dialog-layer
 * (a FluentProvider with z-index 1_000_000) in a useEffect that runs after these layout
 * effects on app startup, so poll for it instead of permanently falling back to document.body
 * — otherwise floating layers opened from inside a dialog render behind it.
 */
export function usePortalContainer() {
  const dialogLayer = useDialogLayer()
  const [container, setContainer] = React.useState<HTMLElement | undefined>(undefined)
  React.useLayoutEffect(() => {
    if (dialogLayer) return
    let cancelled = false
    const adopt = () => {
      const layer = document.getElementById("dialog-layer")
      if (!layer || cancelled) return false
      setContainer(layer)
      return true
    }
    if (adopt()) return
    // #dialog-layer is portaled in a later effect. A one-shot animation frame is cancelled
    // by Strict Mode before that portal exists, so watch the document until it appears.
    const obs = new MutationObserver(() => {
      if (adopt()) obs.disconnect()
    })
    obs.observe(document.body, { childList: true, subtree: true })
    return () => {
      cancelled = true
      obs.disconnect()
    }
  }, [dialogLayer])
  return dialogLayer ?? container
}
